import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  TextField,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import api from '../api';
import { useToast } from '../hooks/useToast';

interface GoodVibesPromptProps {
  open: boolean;
  onClose: () => void;
  targetUserId: string;
  targetName?: string;
  categories: Record<string, string>;
}

const GoodVibesPrompt: React.FC<GoodVibesPromptProps> = ({
  open,
  onClose,
  targetUserId,
  targetName,
  categories,
}) => {
  const { showToast } = useToast();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const placeholders: Record<string, string> = {
    style: 'e.g. "Love the outfit!"',
    smile_energy: 'e.g. "Such a warm smile"',
    voice: 'e.g. "Great voice!"',
    bio_interests: 'e.g. "Great taste in music"',
    photos_adventures: 'e.g. "Love the hiking photos!"',
    general_warmth: 'e.g. "You seem like a really kind person"',
  };

  const handleSend = async () => {
    if (!selectedCategory) return;
    setSending(true);
    try {
      await api.post('/api/compliments', {
        to_user_id: targetUserId,
        category: selectedCategory,
        message: message.trim() || null,
      });
      showToast('Good Vibe sent!', 'success');
      handleClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to send Good Vibe';
      showToast(detail, 'error');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSelectedCategory(null);
    setMessage('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
        <FavoriteBorderIcon sx={{ fontSize: 24, color: "primary.main", display: 'block', mx: 'auto', mb: 1 }} />
        <Typography variant="h6" fontWeight="bold">
          Send a Good Vibe
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {targetName
            ? `Notice anything you liked about ${targetName}? It's anonymous.`
            : "Notice anything you liked? It's anonymous."}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          What stood out to you?
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {Object.entries(categories).map(([key, label]) => (
            <Chip
              key={key}
              label={label}
              variant={selectedCategory === key ? 'filled' : 'outlined'}
              onClick={() => setSelectedCategory(key)}
              sx={{
                ...(selectedCategory === key && {
                  backgroundColor: "primary.main",
                  color: 'white',
                  '&:hover': { backgroundColor: "primary.dark" },
                }),
              }}
            />
          ))}
        </Box>

        {selectedCategory && (
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={3}
            placeholder={placeholders[selectedCategory] || 'Add a short message (optional)'}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            inputProps={{ maxLength: 150 }}
            helperText={`${message.length}/150 (optional)`}
            sx={{ mt: 1 }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 2, px: 3, gap: 1 }}>
        <Button onClick={handleClose} color="inherit" sx={{ color: 'text.secondary' }}>
          Skip
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!selectedCategory || sending}
          startIcon={sending ? <CircularProgress size={16} /> : undefined}
        >
          {sending ? 'Sending...' : 'Send Good Vibe'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoodVibesPrompt;
